import { Injectable } from '@angular/core';
import { DateTimeService } from './date-time.service';
import { PermissionShortName } from './enums';
import { ITokenData, ITokenInfo } from './interfaces';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private tokenInfo?: ITokenInfo;

  constructor(private dateTimeSvc: DateTimeService) {
  }

  setToken(token: string): ITokenInfo {
    this.tokenInfo = this.createTokenInfo(token);
    return this.tokenInfo;
  }

  getUserId(): string | undefined {
    return this.tokenInfo?.data.userId;
  }

  getJobId(): string | undefined {
    return this.tokenInfo?.data.jobId;
  }

  hasExpired(accessToken: string): boolean {
    try {
      const tokenInfo = this.createTokenInfo(accessToken);
      return (this.dateTimeSvc.getNow() >= tokenInfo.data.expires * 1000);
    } catch {
      return true;
    }
  }

  getExpiresAt(): number | undefined {
    return this.tokenInfo?.data.expires;
  }

  getIssuedAt(): number | undefined {
    return this.tokenInfo?.data.issuedAt;
  }

  hasAccess(requiredScopes: PermissionShortName[]): boolean {
    if (!this.tokenInfo) {
      return false;
    }
    const tokenScopes = this.tokenInfo?.data?.permissions || [];
    const tokenJobScopes = this.tokenInfo?.data?.jobPermissions || [];
    for (const scope of requiredScopes) {
      // Either scopes or job scopes must contain one of the required scopes
      if (tokenScopes.includes(scope)) {
        return true;
      }
      if (tokenJobScopes.includes(scope)) {
        return true;
      }
    }
    return false;
  }

  clearToken(): void {
    this.tokenInfo = undefined;
  }

  createTokenInfo(token: string): ITokenInfo {
    const result = {} as ITokenInfo;
    result.value = token;
    const firstDotIndex = token.indexOf('.');
    const firstPart = token.substring(0, firstDotIndex);
    result.header = JSON.parse(this.base64Decode(firstPart));
    const secondDotIndex = token.indexOf('.', firstDotIndex + 1);
    const secondPart = token.substring(firstDotIndex + 1, secondDotIndex);
    const payload = JSON.parse(this.base64Decode(secondPart)) as ITokenPayload;
    result.data = this.convertTokenPayloadToTokenData(payload);
    return result;
  }

  private base64Decode(value: string): string {
    return atob(value);
  }

  private convertTokenPayloadToTokenData(tokenPayload: ITokenPayload): ITokenData {
    const result: ITokenData = {
      audience: tokenPayload.aud,
      expires: tokenPayload.exp,
      issuedAt: tokenPayload.iat,
      issuer: tokenPayload.iss,
      notBefore: tokenPayload.nbf,
      userId: tokenPayload.userId,
      username: tokenPayload.username,
      // roles: this.getArrayFromItem(tokenPayload.roles),
      permissions: this.getArrayFromItem(tokenPayload.permissions) as PermissionShortName[],
      jobPermissions: this.getArrayFromItem(tokenPayload.jobPermissions) as PermissionShortName[],
      jobId: tokenPayload.jobId
    };
    return result;
  }

  private getArrayFromItem(item?: string | string[]): string[] {
    if (!item) {
      return [];
    }
    return Array.isArray(item) ? item : [item];
  }
}

interface ITokenPayload {
  nbf: number;
  exp: number;
  iat: number;
  iss: string;
  aud: string;
  jobId?: string;
  // jobRoles?: string | string[];
  jobPermissions?: string | string[];
  // roles?: string | string[];
  permissions: string | string[];
  userId: string;
  username: string;
}
